import React, { useState } from 'react';
import { 
  Filter, 
  Search, 
  ArrowUpDown,
  SortAsc,
  SortDesc,
  Download,
  FileText,
  FlaskConical
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { useTestResults } from '../hooks/use-test-results';
import { 
  TestCategory as AppTestCategory,
  TestResultStatus as AppTestResultStatus,
  TestResult as AppTestResult
} from '../types/test-result-types';
import EnhancedResultDetails from '../components/test-results/enhanced-result-details';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import GlassCard from '@/components/ui/glass-card';
import Loading from '@/components/ui/Loading';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import PageContainer from '@/components/layout/PageContainer';

// Type definitions
type SortField = 'orderedDate' | 'testName' | 'status';

// Static categories and statuses (Corrected based on type definitions)
const testCategories: (AppTestCategory | 'other')[] = ['cbc', 'bmp', 'cmp', 'lipid-panel', 'thyroid-panel', 'liver-panel', 'a1c', 'coagulation', 'x-ray', 'ct-scan', 'mri', 'ultrasound', 'mammogram', 'dexa', 'pet-scan', 'ecg', 'echo', 'stress-test', 'holter-monitor', 'urinalysis', 'culture', 'biopsy', 'endoscopy', 'colonoscopy', 'pft', 'sleep-study', 'other'];
const testStatuses: AppTestResultStatus[] = ['pending', 'preliminary', 'final', 'amended', 'cancelled', 'error'];

/**
 * TestResults Page - Displays patient test results in a table
 */
const TestResults: React.FC = () => {
  const { 
    filteredResults,
    selectedResult, 
    isLoading, 
    error, 
    filters, 
    updateFilters, 
    resetFilters, 
    selectResult, 
    clearSelectedResult
  } = useTestResults();
  
  const [isFiltersVisible, setIsFiltersVisible] = useState<boolean>(false);
  
  const handleCloseDetails = (): void => {
    clearSelectedResult();
  };
  
  const toggleFilters = (): void => {
    setIsFiltersVisible(!isFiltersVisible);
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    updateFilters({ searchTerm: e.target.value });
  };
  
  const handleCategoryChange = (value: string): void => {
    updateFilters({ category: value as AppTestCategory | 'all' | null });
  };
  
  const handleStatusChange = (value: string): void => {
    updateFilters({ status: value as AppTestResultStatus | 'all' });
  };
  
  const handleSort = (sortBy: SortField): void => {
    const currentSortBy = filters.sortBy;
    const currentDirection = filters.sortDirection;
    let newDirection: 'asc' | 'desc' = 'desc';

    if (currentSortBy === sortBy) {
      newDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newDirection = 'desc';
    }
    updateFilters({ sortBy: sortBy, sortDirection: newDirection });
  };

  const renderSortIcon = (field: SortField) => {
    if (filters.sortBy === field) {
      return filters.sortDirection === 'asc' ? 
        <SortAsc className="ml-1 h-3 w-3" /> : 
        <SortDesc className="ml-1 h-3 w-3" />;
    }
    return <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />;
  };

  // Helper function to check if a result has any abnormal components
  const isResultAbnormal = (result: AppTestResult): boolean => {
    return result.components?.some(comp => 
      ['abnormal', 'high', 'low', 'critical'].includes(comp.status || '')
    ) || false;
  };

  // Get status badge component
  const getStatusBadge = (status: AppTestResultStatus) => {
    let variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" = "secondary";
    switch (status) {
      case 'final': variant = 'success'; break;
      case 'pending': variant = 'default'; break;
      case 'preliminary': variant = 'warning'; break;
      case 'amended': variant = 'info'; break;
      case 'cancelled': case 'error': variant = 'destructive'; break;
    }
    return (
      <Badge variant={variant} className="capitalize">
        {status}
      </Badge>
    );
  };

  return (
    <PageContainer
      title="Test Results"
      description="View and manage your medical test results"
      icon={<FlaskConical className="h-5 w-5" />}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={toggleFilters}>
            <Filter className="h-4 w-4 mr-2" />
            {isFiltersVisible ? 'Hide' : 'Show'} Filters
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download All (Placeholder)
          </Button>
        </div>
      }
    >
      {isFiltersVisible && (
        <GlassCard padding="none" variant="default" className="mb-6 overflow-hidden">
          <div className="flex flex-col space-y-1.5 p-5 pb-4 border-b border-white/10 dark:border-white/5">
             <h3 className="font-semibold leading-none tracking-tight text-lg text-[var(--text-primary)]">Filter & Sort Results</h3>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-white/40 dark:bg-black/10">
            <div className="lg:col-span-2">
              <label htmlFor="search-input" className="text-sm font-medium">Search</label>
              <div className="relative mt-1">
                <Input
                  type="text" 
                  placeholder="Search by test name..."
                  value={filters?.searchTerm || ''}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-4 py-2"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                  <Search className="h-4 w-4" />
                </div>
              </div>
            </div>
            
            <div>
              <label htmlFor="category-filter" className="text-sm font-medium">Category</label>
              <Select value={filters.category || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger aria-label="Filter by category" className="mt-1">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {testCategories.map(cat => (
                    <SelectItem key={cat} value={cat} className="capitalize">{cat.replace('-', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label htmlFor="status-filter" className="text-sm font-medium">Status</label>
              <Select value={filters.status || 'all'} onValueChange={handleStatusChange}>
                <SelectTrigger aria-label="Filter by status" className="mt-1">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {testStatuses.map(status => (
                    <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
             <div className="flex items-end">
                <Button variant="ghost" onClick={resetFilters} size="sm">Reset Filters</Button>
             </div>
          </div>
        </GlassCard>
      )}

      <GlassCard padding="none" variant="default" className="overflow-hidden">
        <div className="p-0 bg-white/40 dark:bg-black/10">
          {isLoading ? (
            <Loading text="Loading test results..." />
          ) : error ? (
            <ErrorMessage message={error.message || "Failed to load results."} />
          ) : filteredResults.length === 0 ? (
            <EmptyState 
              title="No Test Results Found"
              message={filters.searchTerm || filters.category !== 'all' || filters.status !== 'all' ? "Try adjusting your filters." : "You don't have any test results yet."}
              className="py-10"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('testName')}>
                    <div className="flex items-center">Test Name {renderSortIcon('testName')}</div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('orderedDate')}>
                    <div className="flex items-center">Date {renderSortIcon('orderedDate')}</div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('status')}>
                    <div className="flex items-center">Status {renderSortIcon('status')}</div>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResults.map((result: AppTestResult) => {
                  const abnormal = isResultAbnormal(result); // Check if abnormal
                  return (
                    <TableRow 
                      key={result.id} 
                      className={cn(abnormal && "bg-warning-subtle hover:bg-warning-subtle/80")} // Apply conditional class
                    >
                      <TableCell className="font-medium">
                        {/* Optionally add alert icon */} 
                        {/* {abnormal && <BadgeAlert className="h-4 w-4 mr-2 inline-block text-warning" />} */} 
                        {result.testName}
                      </TableCell>
                      <TableCell>
                        {result.orderedDate && isValid(parseISO(result.orderedDate))
                          ? format(parseISO(result.orderedDate), 'MMM d, yyyy')
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{getStatusBadge(result.status)}</TableCell>
                      <TableCell>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => selectResult(result.id)}
                        >
                          <FileText className="h-4 w-4 mr-1" /> View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </GlassCard>

      {/* Detail Modal - Using Enhanced Version */}
      {selectedResult && (
        <EnhancedResultDetails
          result={selectedResult}
          onClose={handleCloseDetails}
        />
      )}
    </PageContainer>
  );
};

export default TestResults;